import React, { useState, useEffect, useRef } from 'react';
import { useAppSelector } from '../../hooks/useAppSelector';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import Toast from '../Toast';
import LoadingOverlay from '../LoadingOverlay';

const POLL_INTERVAL_MS = 30000; // 30 seconds — reduces API cost vs 10s

const OrderFlow = () => {
    const { user, restaurant } = useAppSelector(state => state.auth);
    const restaurantId = restaurant?._id || user?.id;
    
    const [orders, setOrders] = useState([]);
    const [waiterRequests, setWaiterRequests] = useState([]);
    const [tables, setTables] = useState([]);
    const [selectedTableId, setSelectedTableId] = useState('');
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [statusFilter, setStatusFilter] = useState('all');
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [checkedInWaiters, setCheckedInWaiters] = useState([]);
    const [checkedInCooks, setCheckedInCooks] = useState([]);
    const [assigningOrderId, setAssigningOrderId] = useState(null);
    const [updatingWaiterRequestId, setUpdatingWaiterRequestId] = useState(null);
    const [expandedOrderIds, setExpandedOrderIds] = useState(() => new Set());
    const pollIntervalRef = useRef(null);

    const toggleOrderCollapsed = (orderId) => {
        setExpandedOrderIds(prev => {
            const next = new Set(prev);
            if (next.has(orderId)) next.delete(orderId);
            else next.add(orderId);
            return next;
        });
    };

    useEffect(() => {
        if (restaurantId) {
            fetchTables();
        }
    }, [restaurantId]);

    useEffect(() => {
        if (restaurantId) {
            Promise.all([
                axios.get(API_ENDPOINTS.STAFF_CHECKED_IN(restaurantId), { params: { role: 'WAITER' } }),
                axios.get(API_ENDPOINTS.STAFF_CHECKED_IN(restaurantId), { params: { role: 'COOK' } })
            ]).then(([wRes, cRes]) => {
                setCheckedInWaiters(wRes.data || []);
                setCheckedInCooks(cRes.data || []);
            }).catch(() => {
                setCheckedInWaiters([]);
                setCheckedInCooks([]);
            });
        }
    }, [restaurantId]);

    useEffect(() => {
        if (!restaurantId) return;

        fetchOrderFlow(false);
        fetchWaiterRequests();

        const tick = () => {
            if (typeof document !== 'undefined' && document.hidden) return;
            fetchOrderFlow(false);
            fetchWaiterRequests();
        };
        pollIntervalRef.current = setInterval(tick, POLL_INTERVAL_MS);

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchOrderFlow(false);
                fetchWaiterRequests();
            }
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        return () => {
            if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
            document.removeEventListener('visibilitychange', onVisibilityChange);
        };
    }, [restaurantId, statusFilter, selectedTableId]);

    const fetchTables = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.TABLES(restaurantId));
            setTables(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching tables:', error);
            setTables([]);
        }
    };

    const fetchOrderFlow = async (showRefreshingSpinner = false) => {
        try {
            if (showRefreshingSpinner) setRefreshing(true);
            else if (orders.length === 0) setLoading(true);
            const response = await axios.get(API_ENDPOINTS.ORDERS_FLOW(restaurantId), {
                params: {
                    limit: 100,
                    status: statusFilter !== 'all' ? statusFilter : undefined,
                    today: true,
                    ...(selectedTableId ? { tableId: selectedTableId } : {})
                }
            });
            setOrders(response.data);
        } catch (error) {
            showToast('Failed to fetch order flow', 'error');
            console.error('Error fetching order flow:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    const handleRefresh = () => {
        fetchOrderFlow(true);
        fetchWaiterRequests();
        showToast('Refreshed', 'success');
    };

    const handleAssignWaiter = async (orderId, waiterId) => {
        if (!waiterId) return;
        setAssigningOrderId(orderId);
        try {
            await axios.patch(API_ENDPOINTS.ORDER_ASSIGN_WAITER(orderId), { waiterId });
            showToast('Waiter assigned');
            await fetchOrderFlow(false);
        } catch (err) {
            showToast(err.response?.data?.message || 'Failed to assign waiter', 'error');
        } finally {
            setAssigningOrderId(null);
        }
    };

    const handleAssignCook = async (orderId, cookId) => {
        if (!cookId) return;
        setAssigningOrderId(orderId);
        try {
            await axios.patch(API_ENDPOINTS.ORDER_ASSIGN_COOK(orderId), { cookId });
            showToast('Chef assigned');
            await fetchOrderFlow(false);
        } catch (err) {
            showToast(err.response?.data?.message || 'Failed to assign chef', 'error');
        } finally {
            setAssigningOrderId(null);
        }
    };

    const fetchWaiterRequests = async () => {
        try {
            const response = await axios.get(API_ENDPOINTS.WAITER_REQUESTS_BY_RESTAURANT(restaurantId));
            setWaiterRequests(Array.isArray(response.data) ? response.data : []);
        } catch (error) {
            console.error('Error fetching waiter requests:', error);
        }
    };

    const handleWaiterRequestStatusChange = async (requestId, newStatus) => {
        if (!requestId || !newStatus) return;
        setUpdatingWaiterRequestId(requestId);
        try {
            await axios.put(API_ENDPOINTS.WAITER_REQUEST_UPDATE(requestId), {
                status: newStatus
            });
            showToast('Waiter request status updated');
            await fetchWaiterRequests();
            await fetchOrderFlow(false);
        } catch (error) {
            showToast(error.response?.data?.message || 'Failed to update waiter request', 'error');
        } finally {
            setUpdatingWaiterRequestId(null);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    // Normalize id from API (can be object with _id or string) so dropdown value matches options
    const toIdStr = (v) => (v == null || v === '') ? '' : (typeof v === 'object' && v._id != null ? String(v._id) : String(v));

    // Waiter options for an order: checked-in waiters + assigned waiter if not in list (so dropdown shows correct selection)
    const getWaiterOptionsForOrder = (order) => {
        const assigned = order?.assignedWaiterId;
        const assignedIdStr = toIdStr(assigned?._id || assigned);
        const inList = checkedInWaiters.some((w) => toIdStr(w._id) === assignedIdStr);
        if (assignedIdStr && !inList && (assigned?.name || assignedIdStr)) {
            return [{ _id: assignedIdStr, name: assigned?.name || 'Assigned waiter' }, ...checkedInWaiters];
        }
        return checkedInWaiters;
    };

    // Cook options for an order: same so dropdown shows correct selection when chef is assigned
    const getCookOptionsForOrder = (order) => {
        const assigned = order?.assignedCookId;
        const assignedIdStr = toIdStr(assigned?._id || assigned);
        const inList = checkedInCooks.some((c) => toIdStr(c._id) === assignedIdStr);
        if (assignedIdStr && !inList && (assigned?.name || assignedIdStr)) {
            return [{ _id: assignedIdStr, name: assigned?.name || 'Assigned chef' }, ...checkedInCooks];
        }
        return checkedInCooks;
    };

    const getStatusColor = (status) => {
        const colors = {
            PLACED: 'bg-sky-50 text-sky-700 border border-sky-200',
            ACCEPTED: 'bg-amber-50 text-amber-700 border border-amber-200',
            PREPARING: 'bg-orange-100 text-orange-700 border border-orange-200',
            READY: 'bg-violet-50 text-violet-700 border border-violet-200',
            SERVED: 'bg-emerald-50 text-emerald-700 border border-emerald-200',
            CLOSED: 'bg-stone-100 text-stone-600 border border-stone-200'
        };
        return colors[status] || 'bg-stone-100 text-stone-600 border border-stone-200';
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Derive active waiter requests for the banner (keep behaviour: highlight pending ones)
    const activeWaiterRequests = waiterRequests.filter(
        (req) => req && (req.status === 'PENDING' || req.status === 'initiated' || req.status === 'process')
    );

    const formatWaiterRequestStatus = (status) => {
        if (!status) return { label: 'UNKNOWN', classes: 'bg-stone-100 text-stone-700 border border-stone-200' };
        const normalized = String(status).toLowerCase();
        if (normalized === 'pending' || normalized === 'initiated') {
            return { label: 'PENDING', classes: 'bg-amber-50 text-amber-800 border border-amber-200' };
        }
        if (normalized === 'process' || normalized === 'in_process') {
            return { label: 'IN PROCESS', classes: 'bg-sky-50 text-sky-800 border border-sky-200' };
        }
        if (normalized === 'done' || normalized === 'completed') {
            return { label: 'DONE', classes: 'bg-emerald-50 text-emerald-800 border border-emerald-200' };
        }
        return { label: status.toString().toUpperCase(), classes: 'bg-stone-100 text-stone-700 border border-stone-200' };
    };

    const normalizeText = (value) => (value || '').toString().trim().toLowerCase();

    const getRequestOrderId = (req) => {
        if (!req || !req.orderId) return '';
        const id = typeof req.orderId === 'object' && req.orderId._id != null ? req.orderId._id : req.orderId;
        return id ? id.toString() : '';
    };

    // If a customer request doesn't have orderId, attach it to the best matching order
    // using table/customer + nearest order time.
    const inferOrderIdForCustomerRequest = (req, allOrders) => {
        if (!req || !Array.isArray(allOrders) || allOrders.length === 0) return '';
        if (getRequestOrderId(req)) return '';
        if (req.requestType !== 'CUSTOMER_REQUEST') return '';

        const reqTime = req.createdAt ? new Date(req.createdAt).getTime() : NaN;
        const reqTableId = toIdStr(req.tableId?._id || req.tableId);
        const reqMobile = normalizeText(req.customerMobile);
        const reqCustomer = normalizeText(req.customerName);
        const twoHoursMs = 2 * 60 * 60 * 1000;
        const oneDayMs = 24 * 60 * 60 * 1000;

        const candidates = allOrders.filter((order) => {
            const orderTableId = toIdStr(order.tableId?._id || order.tableId);
            const orderMobile = normalizeText(order.mobileNumber);
            const orderCustomer = normalizeText(order.customerName);
            const sameTable = reqTableId && orderTableId && reqTableId === orderTableId;
            const sameMobile = reqMobile && orderMobile && reqMobile === orderMobile;
            const sameCustomer = reqCustomer && orderCustomer && reqCustomer === orderCustomer;
            if (!sameTable && !sameMobile && !sameCustomer) return false;

            if (Number.isNaN(reqTime)) return true;
            const orderTime = order.orderDate ? new Date(order.orderDate).getTime() : NaN;
            if (Number.isNaN(orderTime)) return true;
            // Keep matches reasonably around the order lifecycle.
            return reqTime >= orderTime - twoHoursMs && reqTime <= orderTime + oneDayMs;
        });

        if (candidates.length === 0) return '';
        if (candidates.length === 1) return candidates[0]?._id?.toString() || '';

        if (Number.isNaN(reqTime)) return candidates[0]?._id?.toString() || '';

        let best = null;
        let bestDelta = Number.POSITIVE_INFINITY;
        candidates.forEach((order) => {
            const orderTime = order.orderDate ? new Date(order.orderDate).getTime() : NaN;
            const delta = Number.isNaN(orderTime) ? Number.POSITIVE_INFINITY : Math.abs(reqTime - orderTime);
            if (delta < bestDelta) {
                best = order;
                bestDelta = delta;
            }
        });

        return best?._id?.toString() || '';
    };

    if (loading && orders.length === 0) {
        return (
            <div className="relative min-h-[60vh] overflow-hidden">
                <div className="sticky top-0 z-10 bg-white border-b-2 border-orange-400 shadow-sm">
                    <div className="flex items-center justify-between gap-2 px-3 py-3 sm:px-5 sm:py-4">
                        <h2 className="text-lg sm:text-2xl font-bold text-stone-800">Order Flow</h2>
                    </div>
                </div>
                <div className="flex items-center justify-center py-16">
                    <div className="animate-spin rounded-full h-10 w-10 border-2 border-orange-400 border-t-transparent" />
                </div>
            </div>
        );
    }

    return (
        <div className="relative min-h-[60vh] overflow-hidden">
            {/* Header — same pattern as Manage Menu / Table Management */}
            <div className="sticky top-0 z-10 bg-white border-b-2 border-orange-400 shadow-sm">
                <div className="flex items-center justify-between gap-2 px-3 py-3 sm:px-5 sm:py-4">
                    <div className="flex items-center gap-2 min-w-0">
                        <h2 className="text-lg sm:text-2xl font-bold text-stone-800 truncate">Order Flow</h2>
                        <span className="hidden xs:inline text-xs sm:text-sm text-stone-500 flex-shrink-0">{orders.length} orders</span>
                    </div>
                    <button
                        type="button"
                        onClick={handleRefresh}
                        disabled={refreshing}
                        className="inline-flex items-center justify-center gap-2 px-3 py-2.5 sm:px-4 rounded-xl font-semibold text-xs sm:text-sm bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors shadow-md active:scale-[0.98] min-h-[44px]"
                    >
                        {refreshing ? (
                            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                            <span>↻</span>
                        )}
                        Refresh
                    </button>
                </div>
            </div>

            <div className="p-3 sm:p-5">
                <p className="text-sm text-stone-500 mb-4">Showing today&apos;s orders only.</p>

                {/* Filters — table dropdown + status chips in one row */}
                <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <div className="flex items-center gap-2 flex-shrink-0">
                        <label htmlFor="table-filter" className="text-xs sm:text-sm font-medium text-stone-600 whitespace-nowrap">
                            Table
                        </label>
                        <select
                            id="table-filter"
                            value={selectedTableId}
                            onChange={(e) => setSelectedTableId(e.target.value)}
                            className="border border-stone-200 rounded-xl px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 focus:border-orange-400 min-w-[140px] max-w-[180px] sm:max-w-none bg-white text-stone-800"
                        >
                            <option value="">All tables</option>
                            {tables.map((t) => (
                                <option key={t._id} value={t._id}>
                                    Table {t.tableNumber || t.tableName || t._id}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                        <button
                            onClick={() => setStatusFilter('all')}
                            className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                                statusFilter === 'all'
                                    ? 'bg-orange-500 text-white shadow-md'
                                    : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'
                            }`}
                        >
                            All
                        </button>
                        {['PLACED', 'ACCEPTED', 'PREPARING', 'READY', 'SERVED', 'CLOSED'].map(status => (
                            <button
                                key={status}
                                onClick={() => setStatusFilter(status)}
                                className={`px-3 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all ${
                                    statusFilter === status
                                        ? 'bg-orange-500 text-white shadow-md'
                                        : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'
                                }`}
                            >
                                {status}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Waiter Requests — compact, amber/stone theme (only active/pending) */}
                {activeWaiterRequests.length > 0 && (
                    <div className="mb-6 bg-amber-50/80 border border-amber-200 rounded-xl p-4">
                        <h3 className="text-sm font-bold text-stone-800 mb-3 flex items-center gap-2">
                            <span>🔔</span> Active Waiter Requests ({activeWaiterRequests.length})
                        </h3>
                        <div className="space-y-2">
                            {activeWaiterRequests.map(request => (
                                <div key={request._id} className="bg-white rounded-xl p-3 border border-stone-200 shadow-sm">
                                    <div className="flex justify-between items-start gap-2">
                                        <div className="min-w-0">
                                            <p className="font-semibold text-stone-800">{request.customerName || 'Customer'}</p>
                                            <p className="text-xs sm:text-sm text-stone-600">Table {request.tableId?.tableNumber || 'N/A'}</p>
                                            <p className="text-sm text-stone-700 mt-1">{request.message}</p>
                                            <p className="text-xs text-stone-500 mt-1">{request.requestType} · {formatDate(request.createdAt)}</p>
                                        </div>
                                        <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                            {(() => {
                                                const s = formatWaiterRequestStatus(request.status);
                                                return (
                                                    <span
                                                        className={`px-2 py-1 text-xs font-semibold rounded-lg ${s.classes}`}
                                                    >
                                                        {s.label}
                                                    </span>
                                                );
                                            })()}
                                            <select
                                                value={request.status || 'initiated'}
                                                onChange={(e) => handleWaiterRequestStatusChange(request._id, e.target.value)}
                                                disabled={updatingWaiterRequestId === request._id}
                                                className="text-[11px] border border-stone-200 rounded-md px-2 py-1 bg-white text-stone-700 disabled:opacity-60"
                                                aria-label="Update waiter request status"
                                            >
                                                <option value="initiated">Initiated</option>
                                                <option value="process">In process</option>
                                                <option value="done">Done</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* Orders List */}
                <div className="space-y-4">
                    {orders.length === 0 ? (
                        <div className="text-center py-12 text-stone-500">
                            <p className="text-4xl mb-2">📋</p>
                            <p className="text-sm font-medium">No orders found</p>
                        </div>
                    ) : (
                        orders.map(order => {
                            const isCollapsed = !expandedOrderIds.has(order._id);
                            const orderIdStr = order?._id != null ? order._id.toString() : '';
                            const orderWaiterRequests = waiterRequests.filter((req) => {
                                if (!req || req.requestType !== 'CUSTOMER_REQUEST') return false;
                                const directOrderId = getRequestOrderId(req);
                                if (directOrderId) return directOrderId === orderIdStr;

                                const inferredOrderId = inferOrderIdForCustomerRequest(req, orders);
                                return inferredOrderId === orderIdStr;
                            });
                            return (
                            <div key={order._id} className="bg-white rounded-xl shadow-sm border border-stone-200 border-t-2 border-t-orange-400 overflow-hidden">
                                <div className="p-3 sm:p-4">
                                    <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <h3 className="text-base sm:text-lg font-bold text-stone-800">
                                                Order #{order._id.toString().slice(-6)}
                                            </h3>
                                            <span className={`px-2.5 py-1 rounded-lg text-xs font-semibold ${getStatusColor(order.status)}`}>
                                                {order.status}
                                            </span>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={() => toggleOrderCollapsed(order._id)}
                                            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-stone-100 text-stone-700 hover:bg-stone-200 border border-stone-200 transition-colors flex-shrink-0"
                                            aria-label={isCollapsed ? 'Show details' : 'Hide details'}
                                        >
                                            {isCollapsed ? 'Show' : 'Hide'}
                                        </button>
                                    </div>
                                    {isCollapsed ? (
                                        <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-stone-600">
                                            <span><span className="font-medium text-stone-700">Customer:</span> {order.customerName || 'Guest'}</span>
                                            <span><span className="font-medium text-stone-700">Amount:</span> <span className="font-semibold text-orange-600">₹{order.finalAmount?.toFixed(2) || '0.00'}</span></span>
                                        </div>
                                    ) : (
                                        <>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1 text-sm text-stone-600 mb-3">
                                        <span><span className="font-medium text-stone-700">Customer:</span> {order.customerName || 'Guest'}</span>
                                        <span><span className="font-medium text-stone-700">Table:</span> {order.orderType === 'table' ? (order.tableId?.tableNumber || order.tableId?.displayName || order.tableNumber || 'N/A') : order.orderType === 'counter' ? 'Counter' : 'N/A'}</span>
                                        <span><span className="font-medium text-stone-700">Amount:</span> <span className="font-semibold text-orange-600">₹{order.finalAmount?.toFixed(2) || '0.00'}</span></span>
                                        <span><span className="font-medium text-stone-700">Date:</span> {formatDate(order.orderDate)}</span>
                                    </div>

                                    {/* Assignments */}
                                    <div className="mt-3 pt-3 border-t border-stone-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                        <div className="bg-stone-50 rounded-xl p-3 border border-stone-100">
                                            <p className="text-xs font-medium text-stone-500 mb-1">👨‍🍳 Chef</p>
                                            {order.status !== 'CLOSED' && (checkedInCooks.length > 0 || order.assignedCookId) ? (
                                                <select
                                                    value={toIdStr(order.assignedCookId?._id || order.assignedCookId)}
                                                    onChange={(e) => handleAssignCook(order._id, e.target.value || null)}
                                                    disabled={assigningOrderId === order._id}
                                                    className="text-sm font-semibold w-full border border-stone-200 rounded-lg px-2 py-2 bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 text-stone-800"
                                                >
                                                    <option value="">Not Assigned</option>
                                                    {getCookOptionsForOrder(order).map((c) => (
                                                        <option key={toIdStr(c._id)} value={toIdStr(c._id)}>{c.name}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <p className="text-sm font-semibold text-stone-700">{order.assignedCookId?.name || 'Not Assigned'}</p>
                                            )}
                                            {order.assignedCookId?.email && <p className="text-xs text-stone-500 mt-0.5">{order.assignedCookId.email}</p>}
                                        </div>
                                        <div className="bg-stone-50 rounded-xl p-3 border border-stone-100">
                                            <p className="text-xs font-medium text-stone-500 mb-1">👨‍💼 Waiter</p>
                                            {order.status !== 'CLOSED' && (checkedInWaiters.length > 0 || order.assignedWaiterId) ? (
                                                <select
                                                    value={toIdStr(order.assignedWaiterId?._id || order.assignedWaiterId)}
                                                    onChange={(e) => handleAssignWaiter(order._id, e.target.value || null)}
                                                    disabled={assigningOrderId === order._id}
                                                    className="text-sm font-semibold w-full border border-stone-200 rounded-lg px-2 py-2 bg-white focus:ring-2 focus:ring-orange-400 focus:border-orange-400 text-stone-800"
                                                >
                                                    <option value="">Not Assigned</option>
                                                    {getWaiterOptionsForOrder(order).map((w) => (
                                                        <option key={toIdStr(w._id)} value={toIdStr(w._id)}>{w.name}</option>
                                                    ))}
                                                </select>
                                            ) : (
                                                <p className="text-sm font-semibold text-stone-700">{order.assignedWaiterId?.name || 'Not Assigned'}</p>
                                            )}
                                            {order.assignedWaiterId?.email && <p className="text-xs text-stone-500 mt-0.5">{order.assignedWaiterId.email}</p>}
                                        </div>
                                    </div>

                                    {/* Items */}
                                    {order.items && order.items.length > 0 && (
                                        <div className="mt-3 pt-3 border-t border-stone-100">
                                            <p className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-2">Items</p>
                                            <ul className="divide-y divide-stone-100 rounded-lg border border-stone-200 overflow-hidden">
                                                {order.items.map((item, idx) => (
                                                    <li key={idx} className="flex justify-between items-center px-3 py-2 bg-white text-sm">
                                                        <span className="text-stone-800">{item.name} <span className="text-stone-500">×{item.quantity}</span></span>
                                                        <span className="font-medium text-stone-800">₹{(item.price * item.quantity).toFixed(2)}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    )}

                                    {/* Waiter requests linked to this order (full chat history) */}
                                    {orderWaiterRequests.length > 0 && (
                                        <div className="mt-3 pt-3 border-t border-stone-100">
                                            <p className="text-xs font-medium text-stone-500 uppercase tracking-wide mb-2">
                                                Waiter requests &amp; replies
                                            </p>
                                            <div className="space-y-2">
                                                {orderWaiterRequests.map((req) => {
                                                    const s = formatWaiterRequestStatus(req.status);
                                                    const customerLabel = req.customerName || 'Customer';
                                                    const replyByName = req.assignedWaiterId?.name || 'Owner';
                                                    return (
                                                        <div
                                                            key={req._id}
                                                            className="rounded-lg border border-stone-200 bg-stone-50 px-3 py-2 text-xs sm:text-sm"
                                                        >
                                                            <div className="flex items-center justify-between gap-2 mb-1">
                                                                <div className="font-semibold text-stone-800 truncate">
                                                                    {req.customerName || 'Customer'}{' '}
                                                                    {req.customerMobile && (
                                                                        <span className="text-stone-500 text-[11px]">
                                                                            · {req.customerMobile}
                                                                        </span>
                                                                    )}
                                                                </div>
                                                                <span
                                                                    className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${s.classes}`}
                                                                >
                                                                    {s.label}
                                                                </span>
                                                            </div>
                                                            <p className="text-stone-700">
                                                                <span className="font-medium">{customerLabel}:</span>{' '}
                                                                {req.message || '—'}
                                                            </p>
                                                            {req.reply && (
                                                                <p className="text-stone-700 mt-1">
                                                                    <span className="font-medium">{replyByName}:</span>{' '}
                                                                    {req.reply}
                                                                </p>
                                                            )}
                                                            <p className="text-[11px] text-stone-500 mt-1">
                                                                {req.requestType || 'REQUEST'} · {formatDate(req.createdAt)}
                                                            </p>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    )}

                                    {order.message && (
                                        <div className="mt-3 pt-3 border-t border-stone-100">
                                            <p className="text-xs font-medium text-stone-500 mb-1">Special instructions</p>
                                            <p className="text-sm text-stone-600 italic">{order.message}</p>
                                        </div>
                                    )}
                                        </>
                                    )}
                                </div>
                            </div>
                            );
                        })
                    )}
                </div>
            </div>

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default OrderFlow;
